import { useScan } from './ScanLayout';
import { PageHeader, Section, Banner, Stat, StatGrid } from '../components/Section';
import { ModuleRemediation } from '../components/Remediation';
import { useState, useMemo } from 'react';

export function SubdomainsPage() {
  const scan = useScan();
  const m = scan.modules.subdomains;
  const [filter, setFilter] = useState('');

  const filtered = useMemo(() => {
    if (!m) return [];
    return filter
      ? m.hosts.filter(h => h.toLowerCase().includes(filter.toLowerCase()))
      : m.hosts;
  }, [m, filter]);

  if (!m) return (
    <>
      <PageHeader title="Subdomain Discovery" subtitle="Module result not available." />
      <Section accent="amber"><Banner tone="warn" title="Subdomain analyzer didn't return a result" /></Section>
    </>
  );

  return (
    <>
      <PageHeader
        title="Subdomain Discovery"
        subtitle="Hosts found in public Certificate Transparency logs (crt.sh)"
      />

      <Section title="Overview" accent="blue">
        <StatGrid cols={4}>
          <Stat label="Hosts Found" value={m.count} tone="info" sub="From CT logs" />
          <Stat label="Notable" value={m.notable?.length ?? 0} tone={m.notable?.length ? 'warn' : 'mute'} sub="Internal/dev/admin" />
          <Stat label="Takeover Candidates" value={m.takeover_candidates?.length ?? 0} tone={m.takeover_candidates?.length ? 'bad' : 'ok'} sub="Dangling CNAMEs" />
          <Stat label="Wildcard Certs" value={m.wildcards?.length ?? 0} tone="mute" sub="*.host in CT" />
        </StatGrid>
      </Section>

      {m.takeover_candidates && m.takeover_candidates.length > 0 && (
        <Section title="Possible subdomain takeovers" accent="rose"
          status={{ label: 'Needs verification', tone: 'bad' }}>
          <Banner tone="bad" title={`${m.takeover_candidates.length} host${m.takeover_candidates.length === 1 ? '' : 's'} point to a non-existent target`}>
            A dangling CNAME can let an attacker claim the target and serve content from your subdomain.
            We detect candidates from DNS — verify and remove the record or reclaim the target.
          </Banner>
          <div className="mt-4 space-y-2">
            {m.takeover_candidates.map(t => (
              <div key={t.host} className="tint-bad rounded-lg border p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-sm font-semibold text-rose-900 break-all">{t.host}</span>
                  <span className={`pill ${t.confidence === 'medium' ? 'pill-bad' : 'pill-warn'}`}>{t.confidence} confidence</span>
                  {t.service && <span className="pill pill-info">{t.service}</span>}
                </div>
                <p className="mt-1 font-mono text-xs text-rose-700 break-all">→ {t.cname} (NXDOMAIN)</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {m.wildcard_dns && (
        <Section accent="slate">
          <Banner tone="info" title="Wildcard DNS detected">
            This zone answers arbitrary subdomains with a catch-all record, so dangling-CNAME
            takeover detection is skipped (it would be unreliable).
          </Banner>
        </Section>
      )}

      {m.notable && m.notable.length > 0 && (
        <Section title={`Notable hosts (${m.notable.length})`} accent="amber"
          status={{ label: 'Observed in CT', tone: 'warn' }}>
          <p className="mb-3 text-sm text-ink-2">
            Hostnames whose labels suggest non-production, internal, or admin surface. Their presence in a
            CT log proves a certificate was <em>issued</em> — not that the host is currently live or exposed.
          </p>
          <div className="flex flex-wrap gap-2">
            {m.notable.map(n => (
              <span key={n.host} className="inline-flex items-center gap-1.5 tint-warn rounded-md border px-2.5 py-1 text-xs">
                <span className="font-mono text-amber-900 break-all">{n.host}</span>
                <span className="rounded-sm bg-amber-200/70 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">{n.category}</span>
              </span>
            ))}
          </div>
        </Section>
      )}

      {m.wildcards && m.wildcards.length > 0 && (
        <Section title={`Wildcard certificates (${m.wildcards.length})`} accent="slate">
          <p className="mb-3 text-sm text-ink-2">
            Namespaces covered by a wildcard SAN in Certificate Transparency — one cert
            protects every host directly beneath them.
          </p>
          <div className="flex flex-wrap gap-2">
            {m.wildcards.map(w => (
              <span key={w} className="rounded-md border border-line bg-panel-2 px-2.5 py-1 font-mono text-xs text-ink-2">
                {w}
              </span>
            ))}
          </div>
        </Section>
      )}

      <Section title={`Hosts (${filtered.length})`} accent="blue">
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Filter hosts…"
          className="mb-3 w-full rounded-md border border-line-2 px-3 py-2 text-sm
                     focus:border-sentinel-500 focus:outline-hidden focus:ring-2 focus:ring-sentinel-500/30"
        />
        {filtered.length === 0 ? (
          <Banner tone="info" title="No matching hosts." />
        ) : (
          <div className="max-h-[600px] overflow-y-auto rounded-lg border border-line">
            <ul className="divide-y divide-line font-mono text-sm">
              {filtered.map(h => (
                <li key={h} className="flex items-center px-3 py-2 hover:bg-panel-2">
                  <span className="break-all text-ink-2">{h}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Section>

      {scan.remediations.some(r => r.module === 'subdomains') && (
        <Section title="How to fix" accent="blue">
          <ModuleRemediation remediations={scan.remediations} modules={['subdomains']} />
        </Section>
      )}
    </>
  );
}
